import { useMemo, useState } from "react";
import { Button, Checkbox, Input, Modal, Typography } from "antd";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";

const { Text } = Typography;

type Recipient = { email: string; name: string };

// The attachment is named generically, so the board it came from is identified by the
// subject and message instead.
const defaultSubject = (dashboardName: string) =>
  dashboardName ? `Sandbox Dashboard: ${dashboardName}` : "Sandbox Dashboard";

const defaultMessage = (dashboardName: string) =>
  dashboardName
    ? `Please find the "${dashboardName}" dashboard attached as a PDF.`
    : "Please find the dashboard attached as a PDF.";

/**
 * Team coaches as email recipients, signed-in user first — same ordering as the
 * report page's recipient list (GeneralReportConfig).
 */
function useRecipients(): Recipient[] {
  const coachList = useSelector(
    (reduxState: any) => reduxState.team?.info?.coach_list as any[] | undefined,
  );
  const { user } = useAuth0();

  return useMemo(() => {
    const coaches = (coachList ?? [])
      .filter((coach) => coach?.email)
      .map((coach) => ({
        email: coach.email as string,
        name: `${coach.first_name ?? ""} ${coach.last_name ?? ""}`.trim(),
      }));

    if (!user?.email) return coaches;
    return [
      { email: user.email, name: user.name ?? "" },
      ...coaches.filter((coach) => coach.email !== user.email),
    ];
  }, [coachList, user?.email, user?.name]);
}

/**
 * Recipient / subject / message picker for "Send Email" in the dashboard's Export PDF
 * menu. All state is local, so the caller mounting this fresh on each open resets it.
 * The board name only travels in the subject and message — the send-email endpoint
 * itself is dashboard-agnostic.
 */
export function SendDashboardEmailModal({
  dashboardName,
  sending,
  busy,
  onCancel,
  onSend,
}: {
  dashboardName: string;
  /** A send from this dialog is in flight — blocks dismissal so the request can't outlive it. */
  sending: boolean;
  /** Other PDF generation is running, which would make a send fail. Disables Send only. */
  busy: boolean;
  onCancel: () => void;
  onSend: (payload: { emails: string[]; subject: string; message: string }) => void;
}) {
  const recipients = useRecipients();
  const [selected, setSelected] = useState<string[]>([]);
  const [subject, setSubject] = useState(() => defaultSubject(dashboardName));
  const [message, setMessage] = useState(() => defaultMessage(dashboardName));

  return (
    <Modal
      open
      title="Send Email"
      onCancel={onCancel}
      // Dismissing mid-send would unmount the dialog while the request stays in flight, so
      // the mail would still go out after an apparent cancel. Blocked for that window only
      // — never for `busy`, which would trap the user behind unrelated work.
      maskClosable={!sending}
      closable={!sending}
      keyboard={!sending}
      width={520}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={sending}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          loading={sending}
          disabled={busy || selected.length === 0 || subject.trim().length === 0}
          onClick={() => onSend({ emails: selected, subject, message })}
        >
          Send
        </Button>,
      ]}
    >
      <div className="dashboard-email-form">
        <div className="dashboard-email-field">
          <Text strong>Subject</Text>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
            maxLength={200}
          />
        </div>

        <div className="dashboard-email-field">
          <Text strong>Message</Text>
          <Input.TextArea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Email message"
            autoSize={{ minRows: 3, maxRows: 8 }}
          />
        </div>

        <div className="dashboard-email-field">
          <Text strong>Send {dashboardName || "Dashboard"} as Email to:</Text>
          {recipients.length === 0 ? (
            <Text type="secondary">No email recipients available for this team.</Text>
          ) : (
            <Checkbox.Group
              value={selected}
              onChange={(values) => setSelected(values as string[])}
              className="dashboard-email-recipients"
            >
              {recipients.map((recipient) => (
                <Checkbox key={recipient.email} value={recipient.email}>
                  {recipient.name} <span className="dashboard-email-address">({recipient.email})</span>
                </Checkbox>
              ))}
            </Checkbox.Group>
          )}
        </div>
      </div>
    </Modal>
  );
}
